import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Controller } from 'react-hook-form';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Input, Dialog, Tooltip } from '@dsi/react-eds';
import styles from './EditProductNumberDialog.module.css';
import { useProductNumberForm } from './use-product-number-form';
import './Settings.css';
import { useLoader } from '../../../hooks/useLoader';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';
export interface EditProductNumberDialogProps {
  open: boolean;
  initialProduct: { name: string; sw_product_no: string } | null;
  onSave: (product: { sw_product_no: string; name: string }) => void;
  onClose: () => void;
}

const EditProductNumberDialog: React.FC<EditProductNumberDialogProps> = ({ open, initialProduct, onSave, onClose }) => {
  const methods = useProductNumberForm(initialProduct || undefined);
  const { handleSubmit, formState: { errors, isDirty, isValid }, reset, control } = methods;
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (open && initialProduct) {
      reset(initialProduct);
    }

    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, initialProduct, reset]);

  const editProduct = async (body: { name: string, sw_product_no: string, product_header?: string, release_note_no?: string, webhook?: string }) => {
    showLoader();
    try {
      const resp = await api.post(`api/swgw-product`, {
        data: {
          'products': body
        }
      });
      onSave(body);
      // Only handles one product
      if (resp.data.failedProducts.length > 0) {
        addNotifications([
          {
            title: 'Error - SWGW Product Number & SW Categories',
            icon: 'icon-triangle-warning',
            description: `${resp.data?.failedProducts[0]?.error}` || `Failed to update Product ${body.sw_product_no}`,
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } else {
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-success',
            description: `Product ${body?.sw_product_no} was successfully updated!`,
            timestamp: new Date(),
            color: 'green',
          },
        ]);
      }
    } catch (error: unknown) {
      let errorMsg = `An error occurred while updating the product ${body?.sw_product_no}.`;
      if (error instanceof AxiosError) {
        errorMsg = error?.response?.data?.message || error?.message;
      }
      addNotifications([
        {
          title: 'Error - SWGW Product Number & SW Categories',
          icon: 'icon-triangle-warning',
          description: errorMsg,
          timestamp: new Date(),
          color: 'red',
        },
      ]);
      onClose();
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };
  return ReactDOM.createPortal(
    <div id="edit-product-dialog-1">
    <ReactEDSWrapper
      component={Dialog}
      show={open}
      onClose={onClose}
      title="Edit Product Number"
      subtitle="Product Number Management"
      bottom={
        <ReactEDSWrapper
          component={Button}
          variant="primary"
          className="btn primary"
          type="submit"
          form="edit-product-form"
          disabled={!isDirty || !isValid}
        >
          Save
        </ReactEDSWrapper>
      }
    >
      <form
        id="edit-product-form"
        onSubmit={handleSubmit((data) => {
          editProduct(data);
        })}
        className={styles.dialogForm}
      >
        <label htmlFor="edit-product-number" className={styles.label}>
          Product Number <span className={styles.requiredAsterisk}> *</span>
          <ReactEDSWrapper
            component={Tooltip}
            message="Unique identifier assigned to the product being onboarded"
            pointer
          >
            <span>
              <i className={`icon icon-info ${styles.infoIcon}`}></i>
            </span>
          </ReactEDSWrapper>
        </label>
        <Controller
          name="sw_product_no"
          control={control}
          render={({ field }) => (
            <ReactEDSWrapper
              component={Input}
              id="edit-product-number"
              type="text"
              placeholder="Product number"
              {...field}
            />
          )}
        />
        <div>
          {errors.sw_product_no ? (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
              {typeof errors.sw_product_no.message === 'string' ? errors.sw_product_no.message : 'Required'}
            </span>
          ) : null}
        </div>
        <label htmlFor="edit-product-line" className={styles.label}>
          Description <span className={styles.requiredAsterisk}> *</span>
          <ReactEDSWrapper
            component={Tooltip}
            message={"Specify the description associated with this onboarding configuration"}
            pointer
          >
            <span>
              <i className={`icon icon-info ${styles.infoIcon}`}></i>
            </span>
          </ReactEDSWrapper>
        </label>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <ReactEDSWrapper
              component={Input}
              id="product-name"
              type="text"
              placeholder="Description"
              className={
                (errors.name ? `${styles.inputField} ${styles.inputError}` : styles.inputField) + ' ' + styles.emailInputWide
              }
              {...field}
            />
          )}
        />
      </form>
    </ReactEDSWrapper></div>, document.body
  );
};

export default EditProductNumberDialog;
